import {
  EstimateAssembly,
  EstimateAssemblyItem,
  EstimatePoint,
} from '../../api/protosCompiled/estimateAssembly/estimateAssembly_pb';
import { Object as ProtoObject } from '../../api/protosCompiled/object/object_pb';
import { Estimate } from '../../api/protosCompiled/projectEstimate/projectEstimate_pb';
import { Project } from '../../api/protosCompiled/project/project_pb';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import {
  TakeoffAssemblies,
  TakeoffAssembly,
  TakeoffAssemblyItem,
} from '../../types/AssemblyItemType';
import { ObjectService } from '../../api/ObjectService';
import { useCallback } from 'react';
import {
  useFetchChangeOrderBreakouts,
  useFetchEstimateBreakouts,
} from '../../hooks/useFetchBreakout';

export const createQuickAssembly = (
  selectedObject: ProtoObject.AsObject,
  currentPage: number,
  selectedProject: Project.AsObject,
  selectedEstimate: Estimate.AsObject | null,
  userScale: number
) => {
  const quickAssembly = new EstimateAssembly();
  quickAssembly.setAssemblyname('Quick Assembly');
  quickAssembly.setObjectid(selectedObject?.id ?? '');
  quickAssembly.setProjectid(selectedProject?.id ?? '');
  quickAssembly.setEstimateid(selectedEstimate?.estimateid ?? '');
  quickAssembly.setLastupdated(new Date().toISOString());
  quickAssembly.setTotalsegmentlength(0);
  quickAssembly.setTotalpointcount(0);
  quickAssembly.setTotalsegmentcount(0);
  quickAssembly.setTotalsqft(0);
  quickAssembly.setSegmentlength(0);
  quickAssembly.setSegmentcount(0);
  quickAssembly.setMeasuredarea(0);
  quickAssembly.setPointcount(0);
  quickAssembly.setPointtype('');
  quickAssembly.setPointsize(0);
  quickAssembly.setPointbordercolor('');
  quickAssembly.setPointfillcolor('');
  quickAssembly.setLinesize(0);
  quickAssembly.setLinecolor('');
  quickAssembly.setLinedasharray('');
  quickAssembly.setUserscale(userScale);
  quickAssembly.setPagenumber(currentPage);
  quickAssembly.setAssemblymeasurementtype('count');
  quickAssembly.setImageclassificationid(0);
  quickAssembly.setDatecreated(new Date().toISOString());

  const points = new Array<EstimatePoint>();
  quickAssembly.setPointsList(points);

  const items = new Array<EstimateAssemblyItem>();
  quickAssembly.setItemsList(items);

  const fillOpacity = 0;
  quickAssembly.setFillopacity(fillOpacity);

  return quickAssembly;
};

// Hook implementation with proper dependency tracking
export const useCreateQuickAssembly = () => {
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const userScale = useStore(useUnityBuildStore).userScale;

  return useCallback(
    (selectedObject: ProtoObject.AsObject, currentPage: number) => {
      return createQuickAssembly(
        selectedObject,
        currentPage,
        selectedProject,
        selectedEstimate,
        userScale
      );
    },
    [selectedProject, selectedEstimate, userScale]
  );
};

export const filterAssembliesByBreakout = (
  countedAssemblies: TakeoffAssemblies[] | null,
  selectedObject: any,
  currentPage: number,
  selectedBreakout: any
): TakeoffAssemblies[] => {
  if (!selectedBreakout || !countedAssemblies || !selectedObject) {
    return [];
  }

  const assemblies = ObjectService.filterAssemblyByObjectIdandPageNumber(
    countedAssemblies,
    selectedObject.id,
    currentPage
  );

  // Filter by that breakout ID - check all assemblies and their items
  // Include both standard breakout matches and typical breakout matches
  return assemblies.filter((asm: TakeoffAssemblies) => {
    // Check each assembly in the assembliesList
    return asm.assembliesList.some(assembly => {
      // Check if any item in the assembly matches the selected breakout
      return assembly.itemsList?.some(item => {
        if (!item?.breakout) return false;

        // Direct match on breakout ID
        if (item.breakout.breakoutid === selectedBreakout.id) {
          return true;
        }

        // If the selected breakout is a typical breakout, also match items
        // that have the standard breakout ID that this typical breakout references
        if ((selectedBreakout as any)._isTypical && (selectedBreakout as any).standardBreakoutId) {
          return item.breakout.breakoutid === (selectedBreakout as any).standardBreakoutId;
        }

        // If the selected breakout is a standard breakout, also match items
        // that have typical breakouts referencing this standard breakout
        if (!(selectedBreakout as any)._isTypical && item.breakout.typicalbreakoutid) {
          // This item uses a typical breakout, check if its standard breakout matches
          return item.breakout.breakoutid === selectedBreakout.id;
        }

        return false;
      });
    });
  });
};

// Add the missing utility functions needed by the memoized components
//** Check if there's any item in this assembly that isQuoted */
export const hasQuotedItems = (assembly: TakeoffAssembly) => {
  // if it's an EstimateAssembly => assembly.assembliesList[0].estimateitemsList
  // if it's a ChangeOrderAssembly => assembly.assembliesList[0].changeorderitemsList
  const items = assembly.itemsList;
  return items.some((item: TakeoffAssemblyItem) => item.isquoted === true);
};

/**
 * This function returns the correct fetch hook depending on the presence of an estimate or change order
 * @param selectedEstimate
 * @param selectedChangeOrder
 * @returns
 */
export const useFetchBreakouts = () => {
  const { selectedEstimate, selectedChangeOrder } = useStore(useUnityBuildStore, state => ({
    selectedEstimate: state.selectedEstimate,
    selectedChangeOrder: state.selectedChangeOrder,
  }));
  const fetchEstimateBreakouts = useFetchEstimateBreakouts(selectedEstimate?.estimateid ?? '');
  const fetchChangeOrderBreakouts = useFetchChangeOrderBreakouts(
    selectedChangeOrder?.changeorderid ?? ''
  );
  if (selectedEstimate) {
    return fetchEstimateBreakouts;
  } else if (selectedChangeOrder) {
    return fetchChangeOrderBreakouts;
  }
};
